// Kasse ohne Verbindung (docs/kasse-offline/SPEZIFIKATION.md §14.1): Einstellung je Betrieb, Kopplung Kasse ↔ Gerät,
// Pakete für das Gerät, Herzschlag, Nummernreservierung, Übertragung der Offline-Sätze, Klärfälle, Ausfallnotizen und
// Papier-Notbetrieb. Offline ist ein Störfallprozess: Die Kasse wechselt nur dorthin, wenn das Gerät den Server
// technisch nicht erreicht – es gibt keinen Schalter „ohne Verbindung arbeiten“. Was ändert, bekommt der Assistent nicht.
//
// Die Aktionen baut AP-S (modules/pos/server/offline/**); den Geräte-Nachweis prüft resolveTill() aus AP-R1. Kern-Merkmale
// (AP-K, §14.5 Nr. 4/5; AP-K-wunsch-03, eingetragen von AP-S): tillHeartbeat technicalState + audit 'none' (kein Audit je
// Aufruf; Zustandswechsel schreibt der Handler), offlineReserve/offlineSubmit auditChannel 'pos-offline'.
import { defineContract, defineEvent } from '@shared/contract'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { z } from 'zod'
import { DENOMINATIONS } from '../lib/labels'
import { zEntryEnvelope } from '../lib/offline/format'
import { APPROVAL_FIRM_MIN, OFFLINE_LIMIT_RANGES, PAPER_DEVICE_RESERVE, PAPER_ISSUE_MAX, PAPER_SHEET_MAX_LINES, RESOLUTION_NOTE_MIN, UPLOAD_BATCH } from '../lib/offline/limits'
import {
  CASE_KINDS,
  CASE_RESOLUTIONS,
  CONNECTION_STATES,
  DEVICE_END_KINDS,
  DEVICE_PLATFORMS,
  DISPLAY_MODES,
  OUTAGE_KINDS,
  PAPER_PURPOSES,
  SUBMIT_RESULTS,
  SUBMIT_STOP_REASONS,
  zCaseKind,
  zCaseResolution,
  zConnectionState,
  zDeviceState,
  zEntryChecks,
  zHex64,
  zIsoDateTime,
  zIsoDay,
  zOfflineLimits,
  zOfflineSnapshot,
  zOutageCause,
  zOutageLage,
  zPublicKeySpki,
  zStartpack,
  zTillProof,
} from '../lib/offline/types'
import { zOptionalText, zReason, zRegisterRef } from './common'

// Bausteine für andere Verträge der Kasse und für die Oberfläche
export { zTillEcho, zTillOffline, zTillProof } from '../lib/offline/types'
export { zCaseKind, zCaseResolution, zHex64, zOfflineLimits, zOfflineSnapshot, zStartpack }

const READ = 'pos.shift.read'
const SELL = 'pos.sale.create'
const SETTINGS = 'pos.offline.settings'
const MANAGE = 'pos.offline.manage'
const RESOLVE = 'pos.offline.resolve'

const zFirm = z
  .string({ error: 'Bitte die Steuerkanzlei angeben.' })
  .trim()
  .min(APPROVAL_FIRM_MIN, { error: `Bitte die Steuerkanzlei angeben (mindestens ${APPROVAL_FIRM_MIN} Zeichen).` })
  .max(120, { error: 'Der Name der Kanzlei ist zu lang (höchstens 120 Zeichen).' })

const zNote = (min: number) =>
  z
    .string()
    .trim()
    .max(300, { error: 'Der Text ist zu lang (höchstens 300 Zeichen).' })
    .refine((value) => value.length === 0 || value.length >= min, { error: `Bitte mindestens ${min} Zeichen eingeben.` })

// ---- Einstellung je Betrieb (§11.1) --------------------------------------------------------------------------------

export const offlineSettingsGet = defineContract({
  name: 'pos.offline.settings.get',
  title: 'Betrieb ohne Verbindung anzeigen',
  description: 'Ob der Betrieb Kassen ohne Verbindung zum Server weiterverkaufen lässt, mit Freigabe der Steuerkanzlei und den Grenzen.',
  llm: {
    description:
      'Liefert die Einstellung des Betriebs für Kassen ohne Verbindung zum Server: eingeschaltet ja/nein, Steuerkanzlei und ' +
      'Datum der Freigabe, Grenzen (Höchstdauer in Minuten, Vorgänge je Ausfall, Betrag je Bon in Cent, Auszahlung, ' +
      'Wechselgeld vom Tresor). Offline ist ein Störfall – die Kasse wechselt nur ohne Verbindung von selbst dorthin. ' +
      'Geräte je Kasse liefert pos.offline.device.list.',
    keywords: ['ohne Verbindung', 'offline', 'Offline-Kasse', 'Störfall Kasse'],
  },
  input: z.strictObject({}),
  output: z.object({
    enabled: z.boolean(),
    approvalFirm: z.string().nullable(),
    approvedOn: z.string().nullable(),
    approvalNote: z.string().nullable(),
    limits: zOfflineLimits,
    /** Demo-Mandant: die Freigabe „Demo-Betrieb“ ist zulässig */
    demoTenant: z.boolean(),
    /** feste Freigabe im Code für Kassen im Echtbetrieb (OFFLINE_LIVE_ALLOWED) */
    liveAllowed: z.boolean(),
    responsibility: z.string(),
    updatedAt: z.date().nullable(),
    updatedByName: z.string().nullable(),
    /** null: noch nie gespeichert (Vorgaben) */
    version: z.int().nullable(),
    canEdit: z.boolean(),
  }),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

export const offlineSettingsUpdate = defineContract({
  name: 'pos.offline.settings.update',
  title: 'Betrieb ohne Verbindung einstellen',
  description: 'Schaltet den Betrieb ohne Verbindung für alle Offline-Kassen ein oder aus – nur mit Steuerkanzlei und Datum der Freigabe – und legt die Grenzen fest.',
  llm: {
    description: 'Schaltet den Betrieb der Kassen ohne Verbindung zum Server ein oder aus und setzt die Grenzen. Nur über die Einstellungen der Kasse mit frischer Anmeldung.',
    keywords: ['offline einschalten'],
  },
  input: z
    .strictObject({
      enabled: z.boolean(),
      approvalFirm: zFirm.nullable(),
      approvedOn: zIsoDay.nullable(),
      approvalNote: zOptionalText(300),
      maxOfflineMinutes: z.int().min(OFFLINE_LIMIT_RANGES.maxOfflineMinutes.min, { error: 'Mindestens 30 Minuten.' }).max(OFFLINE_LIMIT_RANGES.maxOfflineMinutes.max, { error: 'Höchstens 24 Stunden.' }),
      maxVorgaenge: z.int().min(OFFLINE_LIMIT_RANGES.maxVorgaenge.min, { error: 'Mindestens 50 Vorgänge.' }).max(OFFLINE_LIMIT_RANGES.maxVorgaenge.max, { error: 'Höchstens 5 000 Vorgänge.' }),
      maxBonCents: z.int().min(OFFLINE_LIMIT_RANGES.maxBonCents.min, { error: 'Mindestens 10,00 €.' }).max(OFFLINE_LIMIT_RANGES.maxBonCents.max, { error: 'Höchstens 10 000,00 €.' }),
      maxEmptiesCents: z.int().min(0, { error: 'Der Betrag darf nicht negativ sein.' }),
      maxPayoutCents: z.int().min(0, { error: 'Der Betrag darf nicht negativ sein.' }),
      maxCashInCents: z.int().min(0, { error: 'Der Betrag darf nicht negativ sein.' }).max(OFFLINE_LIMIT_RANGES.maxCashInCents.max, { error: 'Höchstens 2 000,00 €.' }),
      warnAfterMinutes: z.int().min(OFFLINE_LIMIT_RANGES.warnAfterMinutes.min, { error: 'Mindestens 5 Minuten.' }),
      cardReferenceRequired: z.boolean(),
      outageDocBlocking: z.boolean(),
      expectedVersion: z.int().positive().optional(),
    })
    .superRefine((value, ctx) => {
      if (value.enabled && (!value.approvalFirm || !value.approvedOn)) {
        ctx.addIssue({ code: 'custom', path: ['approvalFirm'], message: 'Offline-Betrieb lässt sich nur mit Steuerkanzlei und Datum der Freigabe einschalten.' })
      }
      if (value.maxEmptiesCents > value.maxBonCents) ctx.addIssue({ code: 'custom', path: ['maxEmptiesCents'], message: 'Die Grenze für Rücknahmen liegt höchstens bei der Grenze je Bon.' })
      if (value.maxPayoutCents > value.maxEmptiesCents) ctx.addIssue({ code: 'custom', path: ['maxPayoutCents'], message: 'Die Grenze für Auszahlungen liegt höchstens bei der Grenze für Rücknahmen.' })
      if (value.warnAfterMinutes > value.maxOfflineMinutes) ctx.addIssue({ code: 'custom', path: ['warnAfterMinutes'], message: 'Der Hinweis kommt spätestens bei der Höchstdauer.' })
    }),
  output: z.object({ version: z.int(), message: z.string() }),
  permission: SETTINGS,
  risk: 'write',
  effects: { securitySensitive: true },
  assistant: 'never',
})

// ---- Geräte (§4) ---------------------------------------------------------------------------------------------------

export const zOfflineDevice = z.object({
  id: zId,
  register: zRegisterRef,
  name: z.string(),
  state: zDeviceState,
  stateLabel: z.string(),
  /** hex(SHA-256(SPKI)) – die ersten 16 Zeichen zur Anzeige */
  fingerprint: z.string(),
  platform: z.enum(DEVICE_PLATFORMS).nullable(),
  displayMode: z.enum(DISPLAY_MODES).nullable(),
  storagePersisted: z.boolean().nullable(),
  pairedAt: z.date(),
  pairedByName: z.string().nullable(),
  endedAt: z.date().nullable(),
  endKind: z.enum(DEVICE_END_KINDS).nullable(),
  endReason: z.string().nullable(),
  blockedReason: z.string().nullable(),
  confirmedSeq: z.int(),
  /** zuletzt gemeldeter Zustand (Herzschlag) */
  lastSeenAt: z.date().nullable(),
  connection: zConnectionState.nullable(),
  queueLength: z.int().nullable(),
  queueOldestAt: z.date().nullable(),
  appBuild: z.string().nullable(),
  nativeJournal: z.boolean().nullable(),
  integrity: z.enum(['ok', 'failed']).nullable(),
  uploadStuck: z.boolean().nullable(),
  openCaseCount: z.int(),
})
export type OfflineDevice = z.output<typeof zOfflineDevice>

export const offlineDeviceList = defineContract({
  name: 'pos.offline.device.list',
  title: 'Offline-Kassen anzeigen',
  description: 'Je Kasse das eingerichtete Gerät mit Zustand, letzter Meldung, wartenden Bons und ob es ohne Verbindung verkaufen darf.',
  llm: {
    description:
      'Listet je Kasse der Standorte, an denen die Person Schichten lesen darf, das eingerichtete Gerät der Offline-Kasse: ' +
      'Name, Zustand (eingerichtet, für Offline gesperrt, abgemeldet, verloren), zuletzt erreichbar, wartende Bons ohne ' +
      'Verbindung, bestätigte Folgenummer, ob der Speicher dauerhaft ist und ob die Kasse ohne Verbindung verkaufen darf ' +
      '(mit Grund). Verwenden für „welche Kasse ist gerade ohne Verbindung“. Klärfälle liefert pos.offline.cases.',
    keywords: ['Offline-Kasse', 'Kassengerät', 'ohne Verbindung', 'wartende Bons'],
  },
  input: z.strictObject({ locationId: zId.optional(), registerId: zId.optional(), includeEnded: z.boolean().default(false) }),
  output: z.object({
    enabled: z.boolean(),
    rows: z.array(
      z.object({
        register: zRegisterRef,
        openShift: z.boolean(),
        device: zOfflineDevice.nullable(),
        offlineAllowed: z.boolean(),
        offlineReason: z.string().nullable(),
        /** die Person darf an dieser Kasse ein Gerät einrichten bzw. abmelden */
        canManage: z.boolean(),
      }),
    ),
    /** abgemeldete und verlorene Geräte (nur mit includeEnded) */
    ended: z.array(zOfflineDevice),
  }),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

export const offlineDevicePair = defineContract({
  name: 'pos.offline.device.pair',
  title: 'Gerät als Offline-Kasse einrichten',
  description: 'Bindet die Kasse an dieses Gerät: Es erhält einen eigenen Schlüssel und verkauft die Kasse künftig allein – mit Verbindung wie ohne.',
  llm: { description: 'Richtet das aufrufende Gerät als Offline-Kasse ein. Nur auf dem Gerät selbst mit frischer Anmeldung.', keywords: ['Offline-Kasse einrichten'] },
  input: z.strictObject({
    registerId: zId,
    name: z.string({ error: 'Bitte einen Gerätenamen eingeben.' }).trim().min(2, { error: 'Bitte einen Gerätenamen eingeben.' }).max(60, { error: 'Der Gerätename ist zu lang (höchstens 60 Zeichen).' }),
    publicKeySpki: zPublicKeySpki,
    platform: z.enum(DEVICE_PLATFORMS),
    displayMode: z.enum(DISPLAY_MODES),
    /** Einrichten im gewöhnlichen Browser-Fenster ausdrücklich bestätigt (§4.2 Nr. 3) */
    browserTabConfirmed: z.boolean(),
    storage: z.strictObject({ persisted: z.boolean(), quotaBytes: z.int().nonnegative(), usageBytes: z.int().nonnegative() }),
    userAgent: z.string().max(400),
    appBuild: z.string().max(200),
  }),
  output: z.object({ deviceId: zId, fingerprint: z.string(), register: zRegisterRef, nonce: z.string(), message: z.string() }),
  permission: MANAGE,
  risk: 'write',
  scope: 'record',
  effects: { securitySensitive: true },
  assistant: 'never',
})

export const offlineDeviceRetire = defineContract({
  name: 'pos.offline.device.retire',
  title: 'Offline-Kasse abmelden',
  description: 'Meldet das Gerät einer Offline-Kasse ab – geordnet (alles übertragen, Schicht geschlossen) oder als defekt nach vollständiger Übertragung.',
  llm: { description: 'Meldet das Gerät einer Offline-Kasse ab. Nur in der Verwaltung der Offline-Kassen mit frischer Anmeldung.', keywords: ['Gerät abmelden'] },
  input: z
    .strictObject({
      deviceId: zId,
      /** orderly: geordnet · defective: Gerät defekt, vollständig übertragen (§4.3) */
      mode: z.enum(['orderly', 'defective']),
      reason: zReason,
      /** nur defective: die Person bestätigt, dass das Gerät nach dem letzten Online-Bon nicht „Ohne Verbindung“ zeigte */
      noOfflineConfirmed: z.literal(true).optional(),
      /** nur defective: Zählung der Lade nach dem Defekt (Stückelung wie beim Abschluss) */
      counts: z
        .array(z.strictObject({ denominationCents: z.int().refine((value) => (DENOMINATIONS as readonly number[]).includes(value), { error: 'Diese Stückelung gibt es nicht.' }), count: z.int().min(0).max(100_000) }))
        .max(DENOMINATIONS.length * 2)
        .optional(),
    })
    .superRefine((value, ctx) => {
      if (value.mode !== 'defective') return
      if (value.noOfflineConfirmed !== true) ctx.addIssue({ code: 'custom', path: ['noOfflineConfirmed'], message: 'Bitte bestätigen Sie, dass das Gerät nach dem letzten Online-Bon nicht ‚Ohne Verbindung‘ gezeigt hat.' })
      if (!value.counts) ctx.addIssue({ code: 'custom', path: ['counts'], message: 'Bitte zählen Sie die Kasse.' })
    }),
  output: z.object({ deviceId: zId, state: zDeviceState, message: z.string() }),
  permission: MANAGE,
  risk: 'write',
  scope: 'record',
  effects: { securitySensitive: true },
  assistant: 'never',
})

export const offlineDeviceReportLost = defineContract({
  name: 'pos.offline.device.reportLost',
  title: 'Offline-Kasse als verloren melden',
  description: 'Meldet das Gerät einer Offline-Kasse als verloren, zerstört oder gelöscht: Nummern werden reserviert, die Kasse geht nach Papierblättern, Zählung und Abschluss außer Betrieb.',
  llm: { description: 'Meldet das Gerät einer Offline-Kasse als verloren. Nur in der Verwaltung der Offline-Kassen mit frischer Anmeldung.', keywords: ['Gerät verloren'] },
  input: z.strictObject({
    deviceId: zId,
    reason: zReason,
    lostAt: zIsoDateTime,
    note: zOptionalText(300),
    /**
     * Schritt „Kasse zählen und abschließen“ (§4.3 Nr. 4–5, AP-S): mit der Zählung schließt die Aktion die offene Schicht
     * (Nummern hinter der Reserve), bestätigt die Lücke und nimmt die Kasse außer Betrieb. Ohne Angabe nur Meldung und
     * Reserve; ein zweiter Aufruf für das schon verlorene Gerät mit `close` schließt ab, sobald die Papierblätter erfasst sind.
     */
    close: z
      .strictObject({
        counts: z.array(z.strictObject({ denominationCents: z.int().refine((value) => (DENOMINATIONS as readonly number[]).includes(value), { error: 'Diese Stückelung gibt es nicht.' }), count: z.int().min(0).max(100_000) })).max(DENOMINATIONS.length * 2),
        transferCents: z.int().min(0),
      })
      .optional(),
  }),
  output: z.object({
    deviceId: zId,
    reservedFrom: z.int().positive(),
    reservedTo: z.int().positive(),
    /** nächste Schritte: Papierblätter erfassen, Kasse zählen und abschließen, Mitteilung */
    caseId: zId.nullable(),
    taskId: zId.nullable(),
    /** Schicht abgeschlossen und Kasse außer Betrieb (mit `close` bzw. ohne offene Schicht) */
    closed: z.boolean(),
    message: z.string(),
  }),
  permission: MANAGE,
  risk: 'destructive',
  scope: 'record',
  effects: { securitySensitive: true },
  assistant: 'never',
})

export const offlineDeviceUnblock = defineContract({
  name: 'pos.offline.device.unblock',
  title: 'Sperre der Offline-Kasse aufheben',
  description: 'Hebt die Sperre eines Geräts für den Betrieb ohne Verbindung auf, nachdem alle Klärfälle „Satz nicht übernommen“ erledigt sind.',
  llm: { description: 'Hebt die Offline-Sperre eines Kassengeräts auf. Nur in der Verwaltung der Offline-Kassen mit frischer Anmeldung.', keywords: ['Sperre aufheben'] },
  input: z.strictObject({ deviceId: zId, reason: zReason }),
  output: z.object({ deviceId: zId, state: zDeviceState, message: z.string() }),
  permission: MANAGE,
  risk: 'write',
  scope: 'record',
  effects: { securitySensitive: true },
  assistant: 'never',
})

// ---- Pakete und Herzschlag (§5, §10.1) ------------------------------------------------------------------------------

export const offlineSnapshot = defineContract({
  name: 'pos.offline.snapshot',
  title: 'Stammdaten für die Kasse laden',
  description: 'Stammdaten-Paket des Standorts der Kasse für den Verkauf ohne Verbindung – nur, wenn sich die Version geändert hat.',
  llm: { description: 'Lädt das Stammdaten-Paket einer Offline-Kasse. Nur für das Gerät der Kasse.', keywords: ['Stammdaten-Paket'] },
  input: z.strictObject({ registerId: zId, have: zHex64.nullable() }),
  output: z.discriminatedUnion('notModified', [
    z.object({ notModified: z.literal(true), version: zHex64 }),
    z.object({ notModified: z.literal(false), snapshot: zOfflineSnapshot }),
  ]),
  permission: SELL,
  risk: 'read',
  scope: 'record',
  assistant: 'never',
})

export const tillHeartbeat = defineContract({
  name: 'pos.till.heartbeat',
  title: 'Herzschlag der Offline-Kasse',
  description: 'Das Gerät meldet seinen technischen Zustand und erhält Startpaket, Nonce und die nächste Vorgangsnummer.',
  llm: { description: 'Technischer Herzschlag einer Offline-Kasse. Nur für das Gerät der Kasse.', keywords: ['Herzschlag'] },
  input: z.strictObject({
    registerId: zId,
    till: zTillProof,
    state: z.strictObject({
      connection: z.enum(CONNECTION_STATES),
      seq: z.int().nonnegative(),
      headHash: zHex64,
      queueLength: z.int().nonnegative(),
      queueOldestAt: zIsoDateTime.nullable(),
      lastBonId: z.int().nonnegative(),
      bonIdUncertain: z.boolean(),
      snapshotVersion: zHex64.nullable(),
      appBuild: z.string().max(200),
      formatVersions: z.array(z.int().positive()).max(5),
      storage: z.strictObject({ persisted: z.boolean(), usageBytes: z.int().nonnegative(), quotaBytes: z.int().nonnegative() }),
      displayMode: z.enum(DISPLAY_MODES),
      clockOffsetMs: z.int().nullable(),
      nativeJournal: z.boolean(),
      integrity: z.enum(['ok', 'failed']),
      uploadStuck: z.boolean(),
      paperUsed: z.array(z.int().positive()).max(20),
    }),
  }),
  output: z.object({
    serverTime: z.date(),
    deviceState: zDeviceState,
    blockedReason: z.string().nullable(),
    confirmedSeq: z.int(),
    confirmedHash: zHex64,
    nextBonId: z.int().positive(),
    snapshotVersion: zHex64,
    /** §5.2 – enthält Nonce, Gültigkeit und den Prüfwert des Rechte-Teils */
    startpack: zStartpack,
  }),
  permission: SELL,
  risk: 'write',
  scope: 'record',
  assistant: 'never',
  // kein Audit je Aufruf: schreibt nur pos_till_device_seen; Zustandswechsel des Geräts schreibt der Handler (§14.5 Nr. 5)
  technicalState: true,
  audit: 'none',
})

// ---- Nummern und Übertragung (§7.4, §10) ---------------------------------------------------------------------------

export const offlineReserve = defineContract({
  name: 'pos.offline.reserve',
  title: 'Vorgangsnummern ohne Verbindung reservieren',
  description: 'Nach der Wiederverbindung reserviert die Kasse die ohne Verbindung vergebenen Vorgangsnummern; neue Vorgänge erhalten Nummern dahinter.',
  llm: { description: 'Reserviert die Vorgangsnummern einer Offline-Episode. Nur für das Gerät der Kasse.', keywords: ['Nummern reservieren'] },
  input: z.strictObject({ registerId: zId, till: zTillProof, outageId: zId, firstOfflineBonId: z.int().positive(), lastBonId: z.int().positive() }),
  output: z.object({ reservedFrom: z.int().positive().nullable(), reservedTo: z.int().positive().nullable(), nextBonId: z.int().positive(), message: z.string() }),
  // Fehler: FORBIDDEN { reason: 'range' } (Umfang über max_vorgaenge + RESERVE_SLACK oder widersprüchlich)
  permission: SELL,
  risk: 'write',
  scope: 'record',
  assistant: 'never',
  auditChannel: 'pos-offline',
})

export const zSubmitCase = z.object({ kind: zCaseKind, blocking: z.boolean(), title: z.string() })

export const offlineSubmit = defineContract({
  name: 'pos.offline.submit',
  title: 'Bons ohne Verbindung übertragen',
  description: 'Überträgt die auf dem Gerät gespeicherten Sätze in Reihenfolge; der Server übernimmt jeden Bon unverändert und legt Klärfälle an, wo etwas abweicht.',
  llm: { description: 'Überträgt die Offline-Sätze einer Kasse. Nur für das Gerät der Kasse bzw. das Einlesen einer Datei-Sicherung.', keywords: ['Offline-Bons übertragen'] },
  input: z.strictObject({
    registerId: zId,
    deviceId: zId,
    /** Pflicht bei source 'device' (Prüfung im Handler) */
    till: zTillProof.optional(),
    // grobe Grenze in Zeichen; Größe in Bytes, Schema, Prüfwert und Signatur prüft der Handler je Satz (§9.4)
    entries: z.array(zEntryEnvelope).min(1).max(UPLOAD_BATCH),
    /** device: das gekoppelte Gerät · file: „Offline-Daten einlesen“ (Recht pos.offline.resolve, im Handler) */
    source: z.enum(['device', 'file']).default('device'),
  }),
  output: z.object({
    results: z.array(
      z.object({
        deviceSeq: z.int(),
        result: z.enum(SUBMIT_RESULTS),
        entryId: zId,
        vorgangId: zId.nullable(),
        bonId: z.int().nullable(),
        receiptNumber: z.string().nullable(),
        cases: z.array(zSubmitCase),
      }),
    ),
    stoppedAt: z.object({ deviceSeq: z.int(), reason: z.enum(SUBMIT_STOP_REASONS), expectedSeq: z.int().nullable() }).nullable(),
    confirmedSeq: z.int(),
    confirmedHash: zHex64,
    nextBonId: z.int().positive(),
    message: z.string(),
  }),
  // Fehler: FORBIDDEN { reason: 'proof' | 'device' }
  permission: SELL,
  risk: 'financial',
  scope: 'record',
  assistant: 'never',
  limits: { maxItems: UPLOAD_BATCH, itemCount: (input) => input.entries.length },
  // Audit und Bestandsbuchungen der Übernahme im Kanal „Kasse (nachgetragen)“; Voraussetzung für offlineTakeover (§14.5)
  auditChannel: 'pos-offline',
})

export const zOfflineEntryRow = z.object({
  id: zId,
  register: zRegisterRef,
  deviceId: zId,
  deviceName: z.string(),
  deviceSeq: z.int(),
  kind: z.string(),
  kindLabel: z.string(),
  formatVersion: z.int(),
  bonId: z.int().nullable(),
  vorgangId: zId.nullable(),
  receiptNumber: z.string().nullable(),
  totalCents: z.int().nullable(),
  operatorName: z.string().nullable(),
  /** Uhr des Geräts */
  deviceTime: z.date().nullable(),
  receivedAt: z.date(),
  receivedByName: z.string().nullable(),
  source: z.enum(['device', 'file']),
  result: z.enum(['accepted', 'quarantined']),
  sigValid: z.boolean(),
  error: z.string().nullable(),
  checks: zEntryChecks,
  caseCount: z.int(),
  /** Rohsatz – nur mit includePayload */
  payload: z.string().nullable(),
  hash: zHex64,
})
export type OfflineEntryRow = z.output<typeof zOfflineEntryRow>

export const offlineEntries = defineContract({
  name: 'pos.offline.entries',
  title: 'Übertragungsprotokoll der Offline-Kassen',
  description: 'Jeder übertragene Satz einer Offline-Kasse mit Folgenummer, Art, Bon, Gerätezeit, Eingang, Ergebnis und Prüfungen.',
  llm: {
    description:
      'Listet die übertragenen Sätze der Offline-Kassen (neueste zuerst): Gerät, Folgenummer, Art (Vorgang, Beginn oder ' +
      'Ende eines Ausfalls, Kassensturz, Papier-Notbetrieb, Hinweis), Vorgangsnummer, Betrag in Cent, Uhr des Geräts, ' +
      'Eingang, wer übertragen hat, Ergebnis (übernommen oder nicht übernommen) und das Prüfprotokoll. Filter: Kasse, ' +
      'Gerät, Ergebnis, Zeitraum, Beleg, Ausfall. Verwenden für „welche Bons kamen ohne Verbindung“. Klärfälle liefert pos.offline.cases.',
    keywords: ['Übertragungsprotokoll', 'Offline-Bons', 'ohne Verbindung', 'Offline-Vorgänge'],
  },
  input: listInput({
    sortBy: ['receivedAt', 'deviceSeq'],
    defaultSort: { by: 'receivedAt', dir: 'desc' },
    filters: {
      registerId: zId.optional(),
      deviceId: zId.optional(),
      result: z.enum(['accepted', 'quarantined']).optional(),
      kind: z.string().max(20).optional(),
      receivedFrom: zIsoDay.optional(),
      receivedTo: zIsoDay.optional(),
      entryId: zId.optional(),
      /** genau der Satz zu diesem Beleg (Kassenbon ohne Verbindung, §15.2; AP-V-wunsch-01) */
      documentId: zId.optional(),
      /** genau die Sätze der Vorgänge eines Ausfalls je Gerät (fiscal_outage, §15.2; AP-V-wunsch-01) */
      outageId: zId.optional(),
      includePayload: z.boolean().default(false),
    },
  }),
  output: listOutput(zOfflineEntryRow),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

// ---- Klärfälle (§10.5) ---------------------------------------------------------------------------------------------

export const zOfflineCaseRow = z.object({
  id: zId,
  kind: z.enum(CASE_KINDS),
  kindLabel: z.string(),
  blocking: z.boolean(),
  state: z.enum(['open', 'resolved']),
  title: z.string(),
  register: zRegisterRef,
  deviceName: z.string().nullable(),
  shiftId: zId.nullable(),
  bonId: z.int().nullable(),
  vorgangId: zId.nullable(),
  entryId: zId.nullable(),
  outageId: zId.nullable(),
  /** Gerätewerte gegen Stammdaten bzw. gespeichertes Startpaket – Aufbau je Art (AP-S) */
  detail: z.record(z.string(), z.unknown()),
  createdAt: z.date(),
  resolvedAt: z.date().nullable(),
  resolvedByName: z.string().nullable(),
  resolution: z.enum(CASE_RESOLUTIONS).nullable(),
  resolutionLabel: z.string().nullable(),
  resolutionDate: z.string().nullable(),
  resolutionNote: z.string().nullable(),
  /** Erledigungen, die diese Person jetzt wählen darf */
  allowed: z.array(z.enum(CASE_RESOLUTIONS)),
})
export type OfflineCaseRow = z.output<typeof zOfflineCaseRow>

export const offlineCases = defineContract({
  name: 'pos.offline.cases',
  title: 'Klärfälle der Offline-Kassen',
  description: 'Abweichungen beim Übertragen der Bons ohne Verbindung – offene zuerst, mit Einzelheiten und möglichen Erledigungen.',
  llm: {
    description:
      'Listet die Klärfälle der Offline-Kassen (offene zuerst): Art (z. B. Preis weicht ab, Nummernlücke, Buchung offen, ' +
      'Satz nicht übernommen), ob er den Kassenabschluss sperrt, Kasse, Vorgangsnummer, Titel, Einzelheiten und Erledigung. ' +
      'Filter: Kasse, Schicht, Art, Zustand. Verwenden für „was ist bei den Offline-Kassen offen“.',
    keywords: ['Klärfall', 'Klärfälle', 'Offline-Kasse', 'Abweichung'],
  },
  input: listInput({
    sortBy: ['createdAt'],
    defaultSort: { by: 'createdAt', dir: 'desc' },
    filters: {
      state: z.enum(['open', 'resolved', 'all']).default('open'),
      registerId: zId.optional(),
      shiftId: zId.optional(),
      kind: zCaseKind.optional(),
      blockingOnly: z.boolean().default(false),
    },
  }),
  output: listOutput(zOfflineCaseRow),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

/** Zähler für die Navigation: offene Klärfälle an den Standorten der Person (Abzeichen „Klärfälle“, §15.2). */
export const offlineCaseCount = defineContract({
  name: 'pos.offline.caseCount',
  title: 'Offene Klärfälle der Offline-Kassen',
  description: 'Anzahl der offenen Klärfälle der Offline-Kassen.',
  llm: { description: 'Zählt die offenen Klärfälle der Offline-Kassen. Für die Navigation; Einzelheiten liefert pos.offline.cases.', keywords: ['Klärfälle'] },
  input: z.strictObject({}),
  output: z.object({ count: z.int(), blocking: z.int() }),
  permission: READ,
  risk: 'read',
  scope: 'any',
  assistant: 'never',
})

export const offlineResolve = defineContract({
  name: 'pos.offline.resolve',
  title: 'Klärfall erledigen',
  description: 'Erledigt einen Klärfall einer Offline-Kasse: zur Kenntnis nehmen, erneut bzw. im ersten offenen Zeitraum buchen, nach Prüfung übernehmen oder eine Lücke bestätigen.',
  llm: { description: 'Erledigt einen Klärfall einer Offline-Kasse. Nur in der Verwaltung der Offline-Kassen.', keywords: ['Klärfall erledigen'] },
  input: z
    .strictObject({
      caseId: zId,
      resolution: zCaseResolution,
      /** book_in_open_period: Beleg- und Zahlungstag im ersten offenen Zeitraum (das Leistungsdatum bleibt) */
      date: zIsoDay.optional(),
      note: zNote(RESOLUTION_NOTE_MIN).optional(),
    })
    .superRefine((value, ctx) => {
      if ((value.resolution === 'book_in_open_period') !== (value.date !== undefined)) {
        ctx.addIssue({ code: 'custom', path: ['date'], message: 'Den Buchungstag gibt es nur bei „Im ersten offenen Zeitraum buchen“ – dort ist er Pflicht.' })
      }
      if (value.resolution === 'documented') ctx.addIssue({ code: 'custom', path: ['resolution'], message: 'Ein Ausfall wird über „Ursache dokumentieren“ erledigt.' })
    }),
  output: z.object({ caseId: zId, state: z.enum(['open', 'resolved']), message: z.string() }),
  permission: RESOLVE,
  risk: 'financial',
  scope: 'record',
  assistant: 'never',
  // „Erneut buchen“ bzw. „Im ersten offenen Zeitraum buchen“ und „Nach Prüfung übernehmen“ sind Teil der Übernahme
  // ohne Verbindung: Audit und Bestand im Kanal pos-offline (Voraussetzung für offlineTakeover, §14.5 Nr. 3)
  auditChannel: 'pos-offline',
})

export const offlineOutageDocument = defineContract({
  name: 'pos.offline.outage.document',
  title: 'Ausfall dokumentieren',
  description: 'Hält zu einem Ausfall Ursache, Maßnahmen, Störungsmeldung und Behebung fest; der erste Eintrag mit Ursache erledigt den Klärfall.',
  llm: { description: 'Dokumentiert Ursache und Maßnahmen eines Ausfalls einer Kasse ohne Verbindung. Nur in der Verwaltung der Ausfälle.', keywords: ['Ausfall dokumentieren'] },
  input: z.strictObject({
    outageId: zId,
    cause: zOutageCause,
    causeText: zOptionalText(300),
    measures: zOptionalText(1_000),
    ticketRef: zOptionalText(80),
    reportedAt: zIsoDateTime.optional(),
    resolvedAt: zIsoDateTime.optional(),
  }),
  output: z.object({ noteId: zId, outageId: zId, caseResolved: z.boolean(), message: z.string() }),
  permission: RESOLVE,
  risk: 'write',
  scope: 'record',
  assistant: 'never',
})

// ---- Papier-Notbetrieb (§11.5) -------------------------------------------------------------------------------------

export const paperFormsIssue = defineContract({
  name: 'pos.paper.forms.issue',
  title: 'Papier-Vordrucke ausgeben',
  description: 'Vergibt fortlaufende Blattnummern für Papier-Vordrucke einer Kasse – als Vorrat zum Drucken oder als Reserve des Geräts.',
  llm: { description: 'Gibt nummerierte Papier-Vordrucke einer Kasse aus. Nur über die Seite „Papier-Vordruck“ bzw. das Gerät der Kasse.', keywords: ['Papier-Vordruck'] },
  input: z
    .strictObject({
      registerId: zId,
      purpose: z.enum(PAPER_PURPOSES),
      count: z.int().min(1, { error: 'Bitte mindestens ein Blatt.' }).max(PAPER_ISSUE_MAX, { error: `Höchstens ${PAPER_ISSUE_MAX} Blätter auf einmal.` }),
      /** Pflicht bei purpose 'device' (nur das gekoppelte Gerät) */
      till: zTillProof.optional(),
    })
    .superRefine((value, ctx) => {
      if (value.purpose !== 'device') return
      if (!value.till) ctx.addIssue({ code: 'custom', path: ['till'], message: 'Die Reserve fordert nur das Gerät der Kasse an.' })
      if (value.count > PAPER_DEVICE_RESERVE) ctx.addIssue({ code: 'custom', path: ['count'], message: `Die Reserve eines Geräts hat höchstens ${PAPER_DEVICE_RESERVE} Blätter.` })
    }),
  output: z.object({ id: zId, from: z.int().positive(), to: z.int().positive(), message: z.string() }),
  permission: SELL,
  risk: 'write',
  scope: 'record',
  assistant: 'never',
})

/** Zeile eines Papierblatts, wie sie darauf steht. */
export const zPaperLine = z.strictObject({
  no: z.int().positive(),
  /** Uhrzeit 'HH:MM' */
  time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, { error: 'Bitte die Uhrzeit als HH:MM angeben.' }),
  text: z.string().trim().min(1, { error: 'Bitte den Artikel angeben.' }).max(200),
  /** sale: Verkauf · return: Rücknahme (Leergut) – mit Vorzeichen im Betrag */
  kind: z.enum(['sale', 'return']),
  /** Menge in Tausendstel */
  qty: z.int().positive(),
  unitPriceCents: z.int().nonnegative(),
  /** Pfand der Zeile brutto (Verkauf) bzw. erstatteter Betrag (Rücknahme) */
  depositCents: z.int().nonnegative(),
  rateBp: z.union([z.literal(1900), z.literal(700), z.literal(0)], { error: 'Bitte wählen Sie 19 %, 7 % oder 0 %.' }),
  method: z.enum(['cash', 'card']),
  /** Belegnummer des Terminals (Karte) */
  reference: zOptionalText(40),
  /** Betrag der Zeile brutto in Cent, Rücknahmen negativ */
  amountCents: z.int(),
})
export type PaperLine = z.output<typeof zPaperLine>

export const paperSheetRecord = defineContract({
  name: 'pos.paper.sheet.record',
  title: 'Papierblatt nacherfassen',
  description: 'Erfasst ein Blatt des Papier-Notbetriebs an der Kasse in ihrer offenen Schicht – als eigener Vorgang mit dem Datum des Blatts – oder erklärt es für unbenutzt.',
  llm: { description: 'Erfasst ein Papierblatt des Notbetriebs an der Kasse nach. Nur an der Kasse bzw. im Ablauf „Gerät verloren“.', keywords: ['Papierblatt nacherfassen'] },
  input: z
    .strictObject({
      registerId: zId,
      /** Pflicht an einer Offline-Kasse mit gekoppeltem Gerät (Ausnahme: Ablauf „Gerät verloren“) */
      till: zTillProof.optional(),
      sheetNo: z.int().positive(),
      /** Tag des Blatts = Leistungs-, Beleg- und Zahlungsdatum */
      businessDate: zIsoDay,
      unused: z.boolean().default(false),
      writtenFrom: zIsoDateTime.optional(),
      writtenTo: zIsoDateTime.optional(),
      writtenBy: zOptionalText(20),
      cashBeginCents: z.int().optional(),
      cashEndCents: z.int().optional(),
      lines: z.array(zPaperLine).max(PAPER_SHEET_MAX_LINES).default([]),
      fileId: zId.optional(),
      note: zOptionalText(300),
    })
    .superRefine((value, ctx) => {
      if (value.unused) {
        if (value.lines.length > 0) ctx.addIssue({ code: 'custom', path: ['lines'], message: 'Ein unbenutztes Blatt hat keine Zeilen.' })
        if ((value.note ?? '').trim().length < RESOLUTION_NOTE_MIN) ctx.addIssue({ code: 'custom', path: ['note'], message: 'Bitte begründen Sie, warum das Blatt unbenutzt ist (mindestens 10 Zeichen).' })
        return
      }
      if (value.lines.length === 0) ctx.addIssue({ code: 'custom', path: ['lines'], message: 'Bitte erfassen Sie die Zeilen des Blatts.' })
      if (!value.writtenFrom || !value.writtenTo) ctx.addIssue({ code: 'custom', path: ['writtenFrom'], message: 'Bitte Beginn und Ende des Papierbetriebs angeben.' })
      if (!value.writtenBy) ctx.addIssue({ code: 'custom', path: ['writtenBy'], message: 'Bitte das Kürzel der Kassenkraft angeben.' })
    }),
  output: z.object({
    sheetId: zId,
    vorgangId: zId.nullable(),
    bonId: z.int().nullable(),
    /** Summen je Steuersatz und Zahlart, wie der Server sie gerechnet hat */
    totals: z.object({
      taxes: z.array(z.object({ rateBp: z.int(), netCents: z.int(), taxCents: z.int(), grossCents: z.int() })),
      payments: z.array(z.object({ method: z.enum(['cash', 'card']), amountCents: z.int() })),
      totalCents: z.int(),
    }),
    message: z.string(),
  }),
  permission: SELL,
  risk: 'financial',
  scope: 'record',
  assistant: 'never',
})

export const paperSheets = defineContract({
  name: 'pos.paper.sheets',
  title: 'Papier-Notbetrieb anzeigen',
  description: 'Je Kasse die ausgegebenen Blattnummern der Papier-Vordrucke: erfasst, unbenutzt oder fehlend.',
  llm: {
    description:
      'Listet je Kasse die ausgegebenen Blattnummern des Papier-Notbetriebs mit Zustand (erfasst, unbenutzt, fehlend), ' +
      'Tag des Blatts, Kürzel, Betrag in Cent und Vorgangsnummer der Nacherfassung sowie die ausgegebenen Reihen. ' +
      'Verwenden für „welche Papierblätter fehlen noch“.',
    keywords: ['Papier-Notbetrieb', 'Papierblatt', 'Vordruck', 'Nacherfassung'],
  },
  input: z.strictObject({ locationId: zId.optional(), registerId: zId.optional(), from: zIsoDay.optional(), to: zIsoDay.optional() }),
  output: z.object({
    rows: z.array(
      z.object({
        register: zRegisterRef,
        sheetNo: z.int(),
        state: z.enum(['recorded', 'unused', 'missing']),
        purpose: z.enum(PAPER_PURPOSES),
        deviceName: z.string().nullable(),
        businessDate: z.string().nullable(),
        writtenBy: z.string().nullable(),
        totalCents: z.int().nullable(),
        vorgangId: zId.nullable(),
        bonId: z.int().nullable(),
        enteredAt: z.date().nullable(),
        enteredByName: z.string().nullable(),
        /** vom Gerät als gedruckt bzw. begonnen gemeldet (paper_begin/paper_end) */
        reportedByDevice: z.boolean(),
      }),
    ),
    ranges: z.array(
      z.object({
        id: zId,
        register: zRegisterRef,
        from: z.int(),
        to: z.int(),
        purpose: z.enum(PAPER_PURPOSES),
        deviceName: z.string().nullable(),
        issuedAt: z.date(),
        issuedByName: z.string().nullable(),
      }),
    ),
  }),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

// ---- Ereignis (§14.4) ----------------------------------------------------------------------------------------------

/** Nach dem Commit einer Übertragung: Mitteilung an die Filialleitung, wenn Klärfälle entstanden. */
export const offlineReceived = defineEvent({
  name: 'pos.offline.received',
  title: 'Bons ohne Verbindung übertragen',
  payload: z.object({
    registerId: zId,
    deviceId: zId,
    fromSeq: z.int(),
    toSeq: z.int(),
    vorgaenge: z.int(),
    firstBonId: z.int().nullable(),
    lastBonId: z.int().nullable(),
    cases: z.array(zSubmitCase),
  }),
})

// ---- Ausfallliste: zusätzliche Angaben je Ausfall (§14.2, contract/fiscal.ts zOutageRow) ------------------------------

/** Felder, die zOutageRow additiv trägt (AP-R2 füllt sie): Art, Gerät, Lage, Gerätezeiten, dokumentiert */
export const zOutageOfflineFields = {
  /** unit = Ausfall der Einheit · offline = Betrieb ohne Verbindung je Gerät */
  outageKind: z.enum(OUTAGE_KINDS).optional(),
  deviceName: z.string().nullable().optional(),
  lage: zOutageLage.nullable().optional(),
  deviceFromAt: z.date().nullable().optional(),
  deviceToAt: z.date().nullable().optional(),
  /** Ursache dokumentiert (nur Art offline; sonst null) */
  documented: z.boolean().nullable().optional(),
}
